import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AuthImage from "@/components/AuthImage";
import { Printer, ArrowLeft, Wrench, PencilSimple, FloppyDisk, X, Plus, MagnifyingGlass, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DiagnosticReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [diag, setDiag] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editLabor, setEditLabor] = useState(false);
  const [laborDraft, setLaborDraft] = useState("");
  const [editExpl, setEditExpl] = useState(false);
  const [explDraft, setExplDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: s }] = await Promise.all([
          api.get(`/diagnostic/${id}`),
          api.get("/shop"),
        ]);
        if (!d.done) {
          toast.error("Diagnosis not yet complete");
          navigate(`/diagnostic/${id}`, { replace: true });
          return;
        }
        setDiag(d);
        setShop(s);
        setLaborDraft(d.final_diagnosis?.estimated_labor_hours || "");
        setExplDraft(d.final_diagnosis?.customer_explanation || "");
      } catch {
        toast.error("Report unavailable");
        navigate("/dashboard", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const saveField = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/diagnostic/${id}/final`, patch);
      setDiag((prev) => ({ ...prev, final_diagnosis: data }));
      toast.success("Saved");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center">
        <div className="font-mono-tech text-sm text-gray-500">Loading report…</div>
      </div>
    );
  }
  if (!diag) return null;

  const f = diag.final_diagnosis || {};
  const created = new Date(diag.created_at).toLocaleString();
  const completed = new Date(diag.updated_at).toLocaleString();
  const hasShop = shop && (shop.shop_name || shop.address || shop.phone || shop.email || shop.logo_path);

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden sticky top-0 z-20 border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to={`/diagnostic/${id}`} className="text-sm text-gray-600 hover:text-black inline-flex items-center gap-1" data-testid="report-back">
            <ArrowLeft weight="bold" className="w-4 h-4" /> Back to session
          </Link>
          <div className="flex items-center gap-2">
            {!hasShop && (
              <Link to="/settings" className="text-xs text-primary hover:underline" data-testid="report-add-shop-link">
                Add shop branding →
              </Link>
            )}
            <Button onClick={() => window.print()} className="bg-primary hover:bg-primary/90 text-white" data-testid="print-btn">
              <Printer weight="bold" className="w-4 h-4 mr-2" /> Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="max-w-4xl mx-auto px-8 py-10 print:py-6" data-testid="report-sheet">
        {/* Letterhead */}
        <header className="flex items-start justify-between gap-6 border-b-2 border-gray-800 pb-6">
          <div className="flex items-start gap-4">
            {shop?.logo_path ? (
              <AuthImage path={shop.logo_path} className="w-20 h-20 object-contain" alt="Shop logo" testid="report-logo" />
            ) : (
              <div className="w-14 h-14 rounded-md bg-primary flex items-center justify-center">
                <Wrench weight="bold" className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <div className="font-display font-bold text-3xl leading-none tracking-tight" data-testid="report-shop-name">
                {shop?.shop_name || "DYNAMIC DIAG"}
              </div>
              {shop?.address && <div className="text-sm text-gray-700 mt-1 whitespace-pre-line">{shop.address}</div>}
              <div className="text-xs text-gray-600 mt-1 font-mono-tech flex flex-wrap gap-x-3">
                {shop?.phone && <span>{shop.phone}</span>}
                {shop?.email && <span>{shop.email}</span>}
              </div>
              {!shop?.shop_name && (
                <div className="text-[10px] font-mono-tech text-gray-500 tracking-widest mt-1">AI DIAGNOSTIC REPORT</div>
              )}
            </div>
          </div>
          <div className="text-right text-xs font-mono-tech text-gray-600 shrink-0">
            <div>REPORT #{diag.diag_id.replace("diag_", "").toUpperCase()}</div>
            <div className="mt-1">CREATED {created}</div>
            <div>COMPLETED {completed}</div>
          </div>
        </header>

        {/* Vehicle & Case */}
        <section className="mt-6 grid grid-cols-2 gap-6">
          <div>
            <SectionLabel>Vehicle</SectionLabel>
            <div className="font-display text-2xl font-semibold leading-tight">
              {diag.vehicle.year} {diag.vehicle.make} {diag.vehicle.model}
            </div>
            <div className="text-xs font-mono-tech text-gray-600 mt-1 flex flex-wrap gap-x-3">
              {diag.vehicle.engine && <span>{diag.vehicle.engine}</span>}
              {diag.vehicle.vin && <span>VIN {diag.vehicle.vin}</span>}
            </div>
          </div>
          <div>
            <SectionLabel>Confidence</SectionLabel>
            <div className="font-display text-2xl font-semibold uppercase">{f.confidence || "—"}</div>
            <div className="text-xs font-mono-tech text-gray-600 mt-1">
              {diag.steps.length} test step{diag.steps.length === 1 ? "" : "s"} · {diag.history.length} answer{diag.history.length === 1 ? "" : "s"}
            </div>
          </div>
        </section>

        {/* Complaint */}
        <section className="mt-6">
          <SectionLabel>Customer Complaint</SectionLabel>
          <div className="text-[15px] leading-relaxed">{diag.complaint}</div>
        </section>

        {/* Observations */}
        {diag.observations && (
          <section className="mt-4">
            <SectionLabel>Observations / DTCs</SectionLabel>
            <div className="font-mono-tech text-sm leading-relaxed">{diag.observations}</div>
          </section>
        )}

        {/* Diagnosis Result */}
        <section className="mt-6 border-2 border-primary rounded-md p-5">
          <SectionLabel className="text-primary">Confirmed Root Cause</SectionLabel>
          <div className="font-display font-bold text-3xl tracking-tight leading-tight mt-1">
            {f.root_cause}
          </div>
        </section>

        {/* Customer Explanation - editable */}
        <section className="mt-6 border border-gray-300 rounded-md p-5 bg-gray-50 print:bg-white break-inside-avoid">
          <div className="flex items-center justify-between mb-2">
            <SectionLabel>What This Means (Plain English)</SectionLabel>
            <button
              type="button"
              onClick={() => setEditExpl((v) => !v)}
              className="print:hidden text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid="edit-explanation-btn"
            >
              <PencilSimple weight="bold" className="w-3.5 h-3.5" /> {editExpl ? "Cancel" : "Edit"}
            </button>
          </div>
          {editExpl ? (
            <div className="print:hidden">
              <Textarea
                value={explDraft}
                onChange={(e) => setExplDraft(e.target.value)}
                className="min-h-28 bg-white text-black border-gray-300"
                data-testid="explanation-textarea"
              />
              <div className="flex justify-end mt-2">
                <Button
                  size="sm"
                  disabled={saving}
                  onClick={async () => {
                    const ok = await saveField({ customer_explanation: explDraft });
                    if (ok) setEditExpl(false);
                  }}
                  className="bg-primary hover:bg-primary/90 text-white"
                  data-testid="save-explanation-btn"
                >
                  <FloppyDisk weight="bold" className="w-4 h-4 mr-1" /> Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-[15px] leading-relaxed text-gray-800" data-testid="customer-explanation">
              {f.customer_explanation || (
                <span className="italic text-gray-500">No customer-friendly explanation yet — click Edit to add one.</span>
              )}
            </div>
          )}
        </section>

        {/* Repair */}
        <section className="mt-6 grid grid-cols-3 gap-4 break-inside-avoid">
          <div className="col-span-2 border border-gray-300 rounded-md p-4">
            <SectionLabel>Repair Recommendation</SectionLabel>
            <div className="text-[15px] leading-relaxed">{f.repair_recommendation}</div>
          </div>
          <div className="border border-gray-300 rounded-md p-4">
            <div className="flex items-center justify-between">
              <SectionLabel>Flat-Rate Labor</SectionLabel>
              <button
                type="button"
                onClick={() => setEditLabor((v) => !v)}
                className="print:hidden text-xs text-primary hover:underline inline-flex items-center gap-1"
                data-testid="edit-labor-btn"
              >
                <PencilSimple weight="bold" className="w-3 h-3" /> {editLabor ? "Cancel" : "Edit"}
              </button>
            </div>
            {editLabor ? (
              <div className="print:hidden mt-1">
                <Input
                  value={laborDraft}
                  onChange={(e) => setLaborDraft(e.target.value)}
                  placeholder="1.0 - 1.5"
                  className="font-mono-tech bg-white text-black border-gray-300"
                  data-testid="labor-input"
                />
                <Button
                  size="sm"
                  disabled={saving}
                  onClick={async () => {
                    const ok = await saveField({ estimated_labor_hours: laborDraft });
                    if (ok) setEditLabor(false);
                  }}
                  className="w-full mt-2 bg-primary hover:bg-primary/90 text-white"
                  data-testid="save-labor-btn"
                >
                  <FloppyDisk weight="bold" className="w-4 h-4 mr-1" /> Save
                </Button>
              </div>
            ) : (
              <div className="font-mono-tech text-2xl font-semibold" data-testid="labor-display">
                {f.estimated_labor_hours || "—"}
                <span className="text-xs text-gray-500 font-normal ml-1.5">hrs</span>
              </div>
            )}
          </div>
        </section>

        {f.notes && (
          <section className="mt-4 border-l-2 border-gray-400 pl-4">
            <SectionLabel>Technician Notes</SectionLabel>
            <div className="text-sm italic text-gray-700">{f.notes}</div>
          </section>
        )}

        {/* Parts */}
        {Array.isArray(f.parts) && f.parts.length > 0 && (
          <section className={`mt-6 break-inside-avoid ${f.show_parts_on_report === false ? "print:hidden" : ""}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <SectionLabel>Parts Required</SectionLabel>
              <div className="print:hidden flex items-center gap-4">
                <label className="text-xs text-gray-600 inline-flex items-center gap-2 cursor-pointer select-none" data-testid="show-parts-toggle-label">
                  <input
                    type="checkbox"
                    checked={f.show_parts_on_report !== false}
                    onChange={(e) => saveField({ show_parts_on_report: e.target.checked })}
                    className="w-4 h-4 accent-primary"
                    data-testid="show-parts-toggle"
                  />
                  Show on customer copy
                </label>
                <span className="text-[10px] font-mono-tech text-gray-500 uppercase tracking-widest">
                  Tap any cell to edit
                </span>
              </div>
            </div>
            {f.show_parts_on_report === false && (
              <div className="print:hidden mt-2 text-xs text-gray-500 italic border-l-2 border-gray-300 pl-3">
                Hidden from the printed customer copy — still visible in the app.
              </div>
            )}
            <PartsTable
              parts={f.parts}
              vehicle={diag.vehicle}
              markupPct={shop?.parts_markup_percent || 0}
              onChange={async (next) => { await saveField({ parts: next }); }}
            />
          </section>
        )}

        {/* Estimate totals */}
        <EstimateTotals
          diag={diag}
          shop={shop}
          finalDiag={f}
          onToggleLabor={(v) => saveField({ show_labor_on_report: v })}
        />

        {/* Test History */}
        <section className="mt-8">
          <SectionLabel>Diagnostic Test History</SectionLabel>
          <div className="mt-2 space-y-3">
            {diag.steps.map((s, i) => {
              const h = diag.history.find((x) => x.step_id === s.id);
              return (
                <div key={s.id || i} className="border border-gray-300 rounded-md p-4 break-inside-avoid">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-display text-lg font-semibold leading-tight">
                      <span className="font-mono-tech text-xs text-gray-500 mr-2">STEP {i + 1}</span>
                      {s.title}
                    </div>
                  </div>
                  {s.goal && <div className="text-sm text-gray-700 mt-1">Goal: {s.goal}</div>}
                  {h && (
                    <div className="mt-2 text-sm">
                      <div><span className="font-semibold">Result: </span>{h.answer}</div>
                      {h.notes && <div className="text-gray-600 mt-0.5">Notes: {h.notes}</div>}
                      {h.reasoning && <div className="text-gray-600 italic mt-1">→ {h.reasoning}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Signature block */}
        <section className="mt-10 grid grid-cols-2 gap-8 break-inside-avoid">
          <div>
            <div className="border-b border-gray-400 h-8" />
            <div className="text-xs font-mono-tech text-gray-600 mt-1">TECHNICIAN SIGNATURE</div>
          </div>
          <div>
            <div className="border-b border-gray-400 h-8" />
            <div className="text-xs font-mono-tech text-gray-600 mt-1">CUSTOMER ACKNOWLEDGMENT</div>
          </div>
        </section>

        <footer className="mt-10 pt-4 border-t border-gray-300 text-[10px] font-mono-tech text-gray-500 flex items-center justify-between">
          <div>Generated by Dynamic Diag · AI-assisted diagnostic aid</div>
          <div>Report #{diag.diag_id.replace("diag_", "").toUpperCase()}</div>
        </footer>
      </div>
    </div>
  );
}

function EstimateTotals({ diag, shop, finalDiag, onToggleLabor }) {
  const f = finalDiag || {};
  if (!shop) return null;

  const laborRate = Number(shop.labor_rate_usd_per_hour) || 0;
  const markupPct = Number(shop.parts_markup_percent) || 0;
  const taxPct = Number(shop.sales_tax_percent) || 0;

  // Labor hours: parse the upper bound of a range like "2.0 - 2.5" -> 2.5
  const nums = String(f.estimated_labor_hours || "").match(/\d+(?:\.\d+)?/g) || [];
  const laborHours = nums.length ? Math.max(...nums.map(Number)) : 0;

  const partsSubtotalRaw = (f.parts || []).reduce((sum, r) => {
    const raw = String(r.estimated_cost_usd || "").split("-")[0];
    const n = parseFloat(raw.replace(/[^\d.]/g, ""));
    const qty = Number(r.quantity) || 1;
    return sum + (isFinite(n) ? n * qty : 0);
  }, 0);
  // Markup is baked directly into the parts subtotal — customer never sees a "markup" line.
  const partsSubtotal = partsSubtotalRaw * (1 + markupPct / 100);

  const showParts = f.show_parts_on_report !== false && partsSubtotal > 0;
  const showLabor = f.show_labor_on_report !== false && laborRate > 0 && laborHours > 0;

  const laborAmount = showLabor ? laborHours * laborRate : 0;
  const preTax = (showParts ? partsSubtotal : 0) + laborAmount;
  const taxAmount = preTax * (taxPct / 100);
  const grandTotal = preTax + taxAmount;

  if (!showParts && !showLabor) {
    // Nothing to price — still let tech toggle labor visibility
    return laborHours > 0 && laborRate === 0 ? (
      <div className="print:hidden mt-4 text-xs text-gray-500 italic border-l-2 border-gray-300 pl-3" data-testid="totals-empty-note">
        Set a labor rate in <a href="/settings" className="text-primary hover:underline">Shop → Pricing</a> to price this estimate.
      </div>
    ) : null;
  }

  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

  return (
    <section className="mt-6 break-inside-avoid" data-testid="estimate-totals">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <SectionLabel>Estimate</SectionLabel>
        {laborHours > 0 && laborRate > 0 && (
          <label className="print:hidden text-xs text-gray-600 inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={f.show_labor_on_report !== false}
              onChange={(e) => onToggleLabor(e.target.checked)}
              className="w-4 h-4 accent-primary"
              data-testid="show-labor-toggle"
            />
            Show labor pricing on customer copy
          </label>
        )}
      </div>

      <div className="mt-2 border border-gray-300 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {showParts && (
              <tr className="border-b border-gray-200">
                <td className="px-3 py-2 text-gray-700">Parts subtotal</td>
                <td className="px-3 py-2 text-right font-mono-tech" data-testid="line-parts-subtotal">{money(partsSubtotal)}</td>
              </tr>
            )}
            {showLabor && (
              <tr className="border-b border-gray-200">
                <td className="px-3 py-2 text-gray-700">
                  Labor
                  <span className="text-xs text-gray-500 font-mono-tech ml-2">
                    {laborHours.toFixed(1)} hr × {money(laborRate)}/hr
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono-tech" data-testid="line-labor">{money(laborAmount)}</td>
              </tr>
            )}
            {taxPct > 0 && (
              <tr className="border-b border-gray-200">
                <td className="px-3 py-2 text-gray-700">Sales tax ({taxPct}%)</td>
                <td className="px-3 py-2 text-right font-mono-tech" data-testid="line-tax">+ {money(taxAmount)}</td>
              </tr>
            )}
            <tr className="bg-gray-100">
              <td className="px-3 py-3 font-display text-lg font-bold uppercase tracking-tight">Grand Total</td>
              <td className="px-3 py-3 text-right font-display text-2xl font-bold" data-testid="line-grand-total">{money(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-1 text-[10px] font-mono-tech text-gray-500 uppercase tracking-widest">
        Estimate — final invoice may vary based on parts availability and actual repair time.
      </div>
    </section>
  );
}

function SectionLabel({ children, className = "" }) {
  return (
    <div className={`text-[10px] font-mono-tech text-gray-500 uppercase tracking-widest mb-1 ${className}`}>
      {children}
    </div>
  );
}

function PartsTable({ parts, vehicle, markupPct = 0, onChange }) {
  // Attach a stable client-side key to each row so React can track edits/removals
  // across re-renders and reorderings without mixing input state between rows.
  const withKeys = (arr) => (arr || []).map((r) => (r && r.__k ? r : { ...r, __k: r?.__k || crypto.randomUUID() }));
  const stripKeys = (arr) => arr.map(({ __k, ...rest }) => rest); // don't leak the key back to the API

  const [rows, setRows] = useState(() => withKeys(parts));
  useEffect(() => { setRows(withKeys(parts)); }, [parts]);

  const vehicleQ = vehicle ? `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim() : "";
  const shopLink = (q) => `https://www.google.com/search?tbm=shop&q=${encodeURIComponent(q)}`;
  const markedUp = (raw) => raw * (1 + (Number(markupPct) || 0) / 100);
  const parseCost = (s) => {
    const first = String(s || "").split("-")[0];
    const n = parseFloat(first.replace(/[^\d.]/g, ""));
    return isFinite(n) ? n : 0;
  };

  const updateRow = (i, patch) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const commit = () => onChange(stripKeys(rows));
  const addRow = () => {
    const next = [...rows, { name: "", oem_part_number: "", aftermarket_options: [], estimated_cost_usd: "", quantity: 1, notes: "", __k: crypto.randomUUID() }];
    setRows(next);
    onChange(stripKeys(next));
  };
  const removeRow = (i) => {
    const next = rows.filter((_, idx) => idx !== i);
    setRows(next);
    onChange(stripKeys(next));
  };

  const subtotal = rows.reduce((sum, r) => {
    const qty = Number(r.quantity) || 1;
    return sum + markedUp(parseCost(r.estimated_cost_usd)) * qty;
  }, 0);

  const editCls = "w-full bg-transparent border-b border-gray-300 focus:border-gray-800 outline-none print:border-0 min-h-6";

  return (
    <div className="mt-2 border border-gray-300 rounded-md overflow-hidden" data-testid="parts-table">
      <table className="w-full text-sm">
        <thead className="bg-gray-100">
          <tr className="text-left text-gray-600">
            <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest">Part</th>
            <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest">OEM #</th>
            <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest">Aftermarket</th>
            <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest text-right w-14">Qty</th>
            <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest text-right w-28">Est. $</th>
            <th className="px-2 py-2 print:hidden w-8"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={p.__k} className="border-t border-gray-200 align-top" data-testid={`report-part-row-${i}`}>
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <input
                    value={p.name || ""}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                    onBlur={commit}
                    placeholder="Part name"
                    className={editCls}
                    data-testid={`part-name-${i}`}
                  />
                  {p.name && (
                    <a
                      href={shopLink(`${p.name} ${vehicleQ}`)}
                      target="_blank" rel="noreferrer"
                      className="print:hidden text-gray-400 hover:text-primary shrink-0"
                      title="Live parts search"
                      data-testid={`part-shop-name-${i}`}
                    >
                      <MagnifyingGlass weight="bold" className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 font-mono-tech">
                <div className="flex items-center gap-1.5">
                  <input
                    value={p.oem_part_number || ""}
                    onChange={(e) => updateRow(i, { oem_part_number: e.target.value })}
                    onBlur={commit}
                    placeholder="—"
                    className={`${editCls} font-mono-tech`}
                    data-testid={`part-oem-${i}`}
                  />
                  {p.oem_part_number && (
                    <a
                      href={shopLink(`${p.oem_part_number} ${vehicleQ}`)}
                      target="_blank" rel="noreferrer"
                      className="print:hidden text-gray-400 hover:text-primary shrink-0"
                      title="Live shopping search for this OEM #"
                      data-testid={`part-shop-oem-${i}`}
                    >
                      <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-xs text-gray-700">
                <input
                  value={(p.aftermarket_options || []).join(", ")}
                  onChange={(e) => updateRow(i, {
                    aftermarket_options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  })}
                  onBlur={commit}
                  placeholder="brand · brand · brand"
                  className={editCls}
                  data-testid={`part-aftermarket-${i}`}
                />
              </td>
              <td className="px-3 py-2 text-right font-mono-tech">
                <input
                  type="number" min="1"
                  value={p.quantity ?? 1}
                  onChange={(e) => updateRow(i, { quantity: Number(e.target.value) })}
                  onBlur={commit}
                  className="w-12 text-right bg-transparent border-b border-gray-300 focus:border-gray-800 outline-none print:border-0"
                  data-testid={`part-qty-${i}`}
                />
              </td>
              <td className="px-3 py-2 text-right font-mono-tech whitespace-nowrap">
                {/* Editor (screen only) — tech enters raw cost */}
                <span className="print:hidden">
                  <span className="text-gray-400 mr-0.5">$</span>
                  <input
                    type="text"
                    value={p.estimated_cost_usd || ""}
                    onChange={(e) => updateRow(i, { estimated_cost_usd: e.target.value })}
                    onBlur={commit}
                    placeholder="55-90"
                    className="w-20 text-right bg-transparent border-b border-gray-300 focus:border-gray-800 outline-none"
                    data-testid={`part-cost-${i}`}
                  />
                  {(Number(markupPct) > 0 && parseCost(p.estimated_cost_usd) > 0) && (
                    <div className="text-[10px] text-gray-500 font-mono-tech mt-0.5">
                      list ${markedUp(parseCost(p.estimated_cost_usd)).toFixed(2)}
                    </div>
                  )}
                </span>
                {/* Print — customer sees the list price (markup baked in) */}
                <span className="hidden print:inline">
                  ${markedUp(parseCost(p.estimated_cost_usd)).toFixed(2)}
                </span>
              </td>
              <td className="px-2 py-2 text-right print:hidden">
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="w-6 h-6 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-500/10 inline-flex items-center justify-center"
                  title="Remove row"
                  data-testid={`part-remove-${i}`}
                >
                  <X weight="bold" className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50">
          <tr className="border-t border-gray-200 print:hidden">
            <td colSpan={6} className="px-3 py-2">
              <button
                type="button"
                onClick={addRow}
                className="text-xs text-primary hover:underline font-mono-tech inline-flex items-center gap-1"
                data-testid="part-add-btn"
              >
                <Plus weight="bold" className="w-3.5 h-3.5" /> ADD PART
              </button>
            </td>
          </tr>
          <tr className="border-t-2 border-gray-400">
            <td className="px-3 py-2" colSpan={3}></td>
            <td className="px-3 py-2 text-right font-mono-tech text-[10px] uppercase tracking-widest text-gray-600">Parts min.</td>
            <td className="px-3 py-2 text-right font-mono-tech font-semibold" data-testid="parts-subtotal">
              ${subtotal.toFixed(2)}
            </td>
            <td className="px-2 print:hidden"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
